import { ActionButtonWithLoading } from '@components/ActionButtonWithLoading/ActionButtonWithLoading';
import { MobileModal } from '@components/MobileModal/MobileModal';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useTopUp } from '@lib/hooks/useTopUp';
import {
  formatDurationSummary,
  type IngestEstimate,
  summarizeDuration,
} from '@lib/utils/ingestEstimate';
import { OwnerAffordability, ownerShortfallOf } from '@lib/utils/ownerAffordability';
import {
  formatAsEstimatedMaximum,
  formatAsEstimatedMinimum,
  formatCreditsWithUnit,
  formatDurationToString,
} from '@lib/utils/units';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { DEFAULT_LIBRARY_NAME } from '@pages/Libraries/components/LibraryCreateModal';
import {
  BaseModal,
  BaseModalRef,
} from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { billingStore } from '@stores/Billing';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { useUploadURL } from '@stores/UploadURL/UploadURLStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { BilledToOwnerNotice } from './BilledToOwnerNotice';
import css from './ChooseMediaModalSummaryConfirm.module.scss';
import { CONTENT_RIGHTS_NOTICE } from './contentRightsNotice';
import { CreditUsageSummary } from './CreditUsageSummary';
import { MediaSourcePreview } from './MediaSourcePreview';
import { SummaryBlock, SummaryBlockVariant } from './SummaryBlock';
import { getUploadDisabledState } from './uploadDisabledState';

interface ChooseMediaModalSummaryConfirmProps {
  open: boolean;
  onClose: () => void;
  onUploadUrl: ({
    newLibraryName,
    createNewLibrary,
    abortController,
  }: {
    newLibraryName?: string;
    createNewLibrary: boolean;
    abortController: AbortController;
  }) => Promise<void>;
  summaryData: Array<{ variant: SummaryBlockVariant; value: string | number }>;
  createNewLibrary?: boolean;
  /** The new-library name (chosen on the upload screen); shown read-only here.
   *  Only rendered when createNewLibrary is set. */
  newLibraryName?: string;
  collectionThumbnail?: string;
  data: {
    creditCost: number;
    ingestEstimate: IngestEstimate;
    storageLeftAfterProcessing: number;
    creditsAfterProcessing: number | null;
    /**
     * True when the target library is billed to another account. The credit/storage usage block
     * describes this user's balance, which isn't the one being charged, so the owner notice
     * replaces it — and neither figure may gate the confirm button.
     */
    billedToAnotherAccount: boolean;
    /** The owner's display name, when the backend gave one. Only read while the flag above is set. */
    ownerName?: string;
    /** Replaces the balance gate the flag above removes. See `ownerAffordability.ts`. */
    ownerAffordability?: OwnerAffordability;
  };
  isUploading: boolean;
  uploadProgress?: { completed: number; total: number } | null;
  uploadAbortController?: AbortController | null;
  onCancel?: () => void; // Called when upload is cancelled to close parent modals
}

export const ChooseMediaModalSummaryConfirm = observer(
  ({
    open,
    onClose,
    onUploadUrl,
    summaryData,
    createNewLibrary = false,
    newLibraryName,
    collectionThumbnail,
    data,
    isUploading,
    uploadProgress,
    uploadAbortController,
    onCancel,
  }: ChooseMediaModalSummaryConfirmProps) => {
    const isMobile = useIsMobile();
    const uploadURLStore = useUploadURL();
    const baseModalRef = useRef<BaseModalRef>(null);

    useModalRegistration('choose-media-summary', open);
    const creditsAfterUpload = data.creditsAfterProcessing;
    const uncertain = data.ingestEstimate.hasUnknownDuration;
    // Storage is the selection's playable length, so it reads the way the summary's length does.
    const storageDuration = summarizeDuration(data.ingestEstimate);
    const storageCost = storageDuration
      ? formatDurationSummary(storageDuration)
      : formatDurationToString(data.ingestEstimate.durationSeconds, { precision: 0, detail: 2 });
    const storageLeftAfterProcessingFormatted = formatDurationToString(
      data.storageLeftAfterProcessing,
      {
        precision: 0,
        detail: 2,
      },
    );

    // Control modal open/close state
    useEffect(() => {
      if (open) {
        baseModalRef.current?.open();
      } else {
        baseModalRef.current?.close();
      }
    }, [open]);

    const disableMainButton = getUploadDisabledState(
      creditsAfterUpload,
      data.storageLeftAfterProcessing,
      summaryData.length,
      uploadURLStore.isAddingUrl || isUploading,
      {
        billedToAnotherAccount: data.billedToAnotherAccount,
        ownerAffordability: data.ownerAffordability,
        ownerName: data.ownerName,
      },
    );

    // Owner-billed: nothing on this account to top up, and `needsAction` staying false is what
    // keeps the confirm button primary and enabled.
    const topUp = useTopUp({
      creditCost: data.creditCost,
      currentCredits: billingStore.creditsLeft ?? 0,
      insufficient:
        !data.billedToAnotherAccount && creditsAfterUpload !== null && creditsAfterUpload <= 0,
      source: 'url-upload-summary',
      ...(data.billedToAnotherAccount
        ? {}
        : {
            storage: {
              afterProcessing: data.storageLeftAfterProcessing,
              duration: data.ingestEstimate.durationSeconds,
            },
          }),
    });

    const libraryName = newLibraryName?.trim() || DEFAULT_LIBRARY_NAME;

    const handleConfirmUpload = async () => {
      trackingStore.chooseMediaSummaryConfirmed();
      const abortController = new AbortController();
      await onUploadUrl({
        newLibraryName: libraryName,
        createNewLibrary: createNewLibrary,
        abortController,
      });
    };

    const handleCancel = () => {
      trackingStore.chooseMediaSummaryCancelled();
      // Abort the upload if it's in progress
      if (uploadAbortController && isUploading) {
        uploadAbortController.abort();
      }
      // Close this modal (which is the last one)
      onClose();
      // Also close parent modals (including MediaUploadBox)
      onCancel?.();
    };

    const handleModalClose = () => {
      // If uploading, cancel it
      if (uploadAbortController && isUploading) {
        uploadAbortController.abort();
      }
      onClose();
      // Only close parent modals when the user actually closed this modal (it was open),
      // not when we're syncing state (useEffect called close() because open is false).
      if (open) {
        onCancel?.();
      }
    };

    const summaryContent = (
      <>
        {createNewLibrary && (
          <div className={css.formFieldAndLabel}>
            <label className={css.label}>New library</label>
            <div className={css.readOnlyValue}>{libraryName}</div>
          </div>
        )}
        {(uploadURLStore.collectionType === 'channel' ||
          uploadURLStore.collectionType === 'playlist') && (
          <div className={css.formFieldAndLabel}>
            <label htmlFor="collection-url">
              {uploadURLStore.collectionType === 'channel' ? 'Channel' : 'Playlist'}
            </label>
            <MediaSourcePreview
              label={uploadURLStore.collectionType === 'channel' ? 'Channel' : 'Playlist'}
              image={uploadURLStore.thumbnail ?? collectionThumbnail}
              circular={uploadURLStore.collectionType === 'channel'}
              title={uploadURLStore.collectionName}
              subtitle={uploadURLStore.url}
            />
          </div>
        )}
        <div className={css.formFieldAndLabel}>
          <label htmlFor="summary-upload">Upload summary</label>
          <div className={css.summaryUploadContainer}>
            {summaryData?.map((item, index) => (
              <SummaryBlock key={index} variant={item.variant} value={item.value} />
            ))}
          </div>
        </div>
        <div className={css.formFieldAndLabel}>
          <label htmlFor="summary-upload">Credit and storage usage</label>
          {/* Cost survives an owner-billed upload — it describes the media. "Remaining after"
              doesn't: it subtracts from this user's balance, not the one being charged, and
              would otherwise render "Not enough credits" over an upload the owner is paying
              for. The notice takes its place. */}
          <CreditUsageSummary
            cost={{
              label: 'Cost for adding these media',
              items: [
                {
                  text: formatAsEstimatedMinimum({
                    formatted: formatCreditsWithUnit(data.creditCost, 'up'),
                    uncertain,
                    approximate: true,
                  }),
                },
                { text: `${storageCost} storage` },
              ],
            }}
            remaining={
              data.billedToAnotherAccount
                ? undefined
                : {
                    label: 'Remaining after adding these media',
                    items: [
                      {
                        text:
                          creditsAfterUpload !== null && creditsAfterUpload >= 0
                            ? formatAsEstimatedMaximum({
                                formatted: formatCreditsWithUnit(creditsAfterUpload, 'down'),
                                uncertain,
                                approximate: true,
                              })
                            : 'Not enough credits',
                        variant:
                          creditsAfterUpload !== null && creditsAfterUpload < 0 ? 'red' : 'green',
                      },
                      {
                        text:
                          data.storageLeftAfterProcessing !== null &&
                          data.storageLeftAfterProcessing >= 0
                            ? `${formatAsEstimatedMaximum({ formatted: storageLeftAfterProcessingFormatted, uncertain })} storage`
                            : 'Not enough storage space',
                        variant: data.storageLeftAfterProcessing < 0 ? 'red' : 'green',
                      },
                    ],
                  }
            }
          />
          {data.billedToAnotherAccount && (
            <BilledToOwnerNotice
              ownerName={data.ownerName}
              shortfall={ownerShortfallOf(data.ownerAffordability)}
              className={css.billedToOwnerNotice}
            />
          )}
        </div>
      </>
    );

    const actionButtons = (
      <>
        {!(isMobile && topUp.needsAction && !isUploading) && (
          <ActionButton
            variant="secondary"
            onClick={isUploading ? handleCancel : onClose}
            disabled={uploadURLStore.isAddingUrl && !isUploading}
            padding={32}
          >
            {isUploading ? 'Cancel' : 'Go back'}
          </ActionButton>
        )}
        {topUp.needsAction && topUp.buttonLabel && !isUploading && (
          <Tooltip title={topUp.tooltip ?? ''} placement="top" arrow>
            <span>
              <ActionButton onClick={topUp.handleTopUp} disabled={topUp.isToppingUp} padding={32}>
                {topUp.buttonLabel}
              </ActionButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={disableMainButton.reason} placement="top" arrow>
          <span>
            <ActionButtonWithLoading
              onClick={handleConfirmUpload}
              disabled={!uploadURLStore.uploadButtonActive || disableMainButton.value}
              variant={topUp.needsAction && !isUploading ? 'secondary' : undefined}
              padding={32}
              isLoading={isUploading}
              progress={
                uploadProgress && uploadProgress.total > 0
                  ? Math.round((uploadProgress.completed / uploadProgress.total) * 100)
                  : undefined
              }
            >
              Add to library
            </ActionButtonWithLoading>
          </span>
        </Tooltip>
      </>
    );

    const rightsNotice = <p className={css.rightsNotice}>{CONTENT_RIGHTS_NOTICE}</p>;

    if (isMobile) {
      return (
        <MobileModal
          open={open}
          onClose={handleModalClose}
          title="Confirm add to library"
          subtitle="Review what you are about to add to your library before proceeding."
          footer={
            <div className={css.mobileFooter}>
              {rightsNotice}
              <div className={css.mobileFooterButtons}>{actionButtons}</div>
            </div>
          }
        >
          <div className={css.mobileContent}>{summaryContent}</div>
        </MobileModal>
      );
    }

    return (
      <BaseModal
        ref={baseModalRef}
        onClose={handleModalClose}
        title={'Confirm add to library'}
        subtitle={'Review what you are about to add to your library before proceeding.'}
        className={css.modal}
      >
        {summaryContent}
        {rightsNotice}
        <div className={css.actionButtons}>{actionButtons}</div>
      </BaseModal>
    );
  },
);
